import React, { useCallback, useEffect, useRef, useState } from 'react'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import { useSelector } from 'react-redux'
import {
  AgentChatWindow,
  AgentFixedContainer,
  AgentWindow,
  AgentWindowWrapper,
  CloseButton,
} from 'src/agent/AgentWrapper.style'
import { createSetIsAgentOpen } from 'src/agent/redux/agent.actions'
import {
  selectAgentShortcut,
  selectIsAgentOpen,
} from 'src/agent/redux/agent.selectors'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgClose from 'src/components/Svgs/SvgClose'
import { FadeWithFrozenExit } from 'src/components/TransitionWithFrozenExit/TransitionWithFrozenExit'
import { useBackgroundDispatch } from 'src/hooks'
import { css } from 'styled-components'

const PADDING = 8

export const AgentWrapper: React.FC = () => {
  const agentIsOpen = useSelector(selectIsAgentOpen)
  const agentShortcut = useSelector(selectAgentShortcut)
  const dispatch = useBackgroundDispatch()
  const ref = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [position, setPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    if (agentShortcut) {
      const parentEl = document.getElementById(agentShortcut.refId)
      if (parentEl && ref.current) {
        const parentRect = parentEl.getBoundingClientRect()
        const childRect = ref.current.getBoundingClientRect()
        let x = 0
        let y = 0

        switch (agentShortcut.placement) {
          case 'top':
            x = parentRect.left + (parentRect.width - childRect.width) / 2
            y = parentRect.top - PADDING
            break
          case 'bottom':
            x = parentRect.left + (parentRect.width - childRect.width) / 2
            y = parentRect.bottom + PADDING
            break
          case 'left':
            x = parentRect.left - PADDING - childRect.width
            y = parentRect.top + (parentRect.height - childRect.height) / 2
            break
          case 'right':
            x = parentRect.right + PADDING
            y = parentRect.top + (parentRect.height - childRect.height) / 2
            break
        }

        // Adjust position to fit within the window
        if (x < childRect.width / 2) {
          x = PADDING
        } else if (x + childRect.width / 2 > window.innerWidth) {
          x = window.innerWidth - childRect.width - PADDING
        }

        if (y < 0) {
          y = PADDING
        } else if (y + childRect.height > window.innerHeight) {
          y = window.innerHeight - childRect.height - PADDING
        }

        handleDrag(undefined, {
          x,
          y,
          deltaX: 0,
          deltaY: 0,
          lastX: 0,
          lastY: 0,
          node: ref.current,
        })
      }
      dispatch(createSetIsAgentOpen(true))
    }
  }, [agentShortcut, ref.current])

  const handleDrag = useCallback(
    (_: DraggableEvent | undefined, data: DraggableData) => {
      setPosition({ x: data.x, y: data.y })
    },
    [],
  )

  useEffect(() => {
    if (!ref.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      if (!agentIsOpen) return
      for (const entry of entries) {
        const rect = entry.contentRect
        // ensure resized position is within the window
        setPosition((prev) => {
          return {
            x: Math.min(
              Math.max(prev.x, PADDING),
              window.innerWidth - rect.width - PADDING,
            ),
            y: Math.min(
              Math.max(prev.y, PADDING),
              window.innerHeight - rect.height - PADDING,
            ),
          }
        })
      }
    })
    resizeObserver.observe(ref.current)
    return () => resizeObserver.disconnect()
  }, [ref.current, agentIsOpen])

  return (
    <AgentFixedContainer>
      <Draggable
        position={position}
        onDrag={handleDrag}
        enableUserSelectHack={false}
        cancel={'input, span'}
      >
        <AgentWindowWrapper ref={wrapperRef}>
          <FadeWithFrozenExit in={agentIsOpen && !!agentShortcut} unmountOnExit>
            <AgentWindow placement={agentShortcut?.placement} ref={ref}>
              <div
                css={css`
                  display: flex;
                  width: 100%;
                  justify-content: flex-end;
                `}
              >
                <HoverableLink
                  ariaLabelSource={'Close'}
                  onClick={(): void => {
                    dispatch(createSetIsAgentOpen(false))
                  }}
                >
                  <CloseButton>
                    <SvgClose />
                  </CloseButton>
                </HoverableLink>
              </div>
              <AgentChatWindow />
            </AgentWindow>
          </FadeWithFrozenExit>
        </AgentWindowWrapper>
      </Draggable>
    </AgentFixedContainer>
  )
}

export default AgentWrapper
